// pages/Campaigns/Gmail/GmailReplyWizard.js
import React, { useState, useEffect, useMemo } from 'react';
import {
    Modal as MuiModal,
    Box,
    Button,
    Stepper,
    Step,
    StepLabel,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Checkbox,
    CircularProgress,
    Typography,
    Alert,
    Divider,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Collapse
} from '@mui/material';
import { Search as SearchIcon, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Image from '@tiptap/extension-image';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import TiptapEditor from '../../../components/Common/TiptapEditor'; // Assuming you create this component

export default function GmailReplyWizard({ isOpen, onClose, selectedEmails, chatbotId, onSuccess }) {
    const [activeStep, setActiveStep] = useState(0);
    const [dataSource, setDataSource] = useState('existing'); // 'existing' or 'knowledge_base'
    const [knowledgeBaseItems, setKnowledgeBaseItems] = useState([]);
    const [selectedKBItems, setSelectedKBItems] = useState([]);
    const [loadingKB, setLoadingKB] = useState(false);
    const [generatingReply, setGeneratingReply] = useState(false);
    const [sendingReplies, setSendingReplies] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [trainingAI, setTrainingAI] = useState(false);
    const [error, setError] = useState('');
    const [generatedReplies, setGeneratedReplies] = useState({});
    const [currentEmailIndex, setCurrentEmailIndex] = useState(0);

    const [kbSearchQuery, setKbSearchQuery] = useState('');
    const [expandedKBItem, setExpandedKBItem] = useState(null);

    const steps = ['Select Data Source', 'Generate Reply', 'Review & Send', 'Train AI (Optional)'];

    const editor = useEditor({
        extensions: [
            StarterKit.configure({}),
            Underline,
            Table.configure({ resizable: true }),
            TableCell, TableHeader, TableRow,
            Link.configure({
                openOnClick: false,
                autolink: true,
                linkOnPaste: true,
                HTMLAttributes: {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                },
            }),
            Image.configure({
                inline: true,
                allowBase64: true,
                HTMLAttributes: {
                    class: 'tiptap-image',
                },
            }),
        ],
        content: '',
    });

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '90%', md: '80%' },
        maxWidth: '1200px',
        height: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 3,
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        outline: 'none',
    };

    // Fetch knowledge base items when user selects that option
    useEffect(() => {
        if (dataSource === 'knowledge_base' && knowledgeBaseItems.length === 0) {
            fetchKnowledgeBase();
        }
    }, [dataSource]);

    // Update editor when current email changes
    useEffect(() => {
        if (editor && selectedEmails.length > 0 && selectedEmails[currentEmailIndex]) {
            const currentEmail = selectedEmails[currentEmailIndex];
            const reply = generatedReplies[currentEmail.id];
            if (reply) {
                editor.commands.setContent(formatAiReplyForEditor(reply));
            } else {
                editor.commands.setContent('<p></p>');
            }
        }
    }, [editor, currentEmailIndex, generatedReplies, selectedEmails]);

    const fetchKnowledgeBase = async () => {
        setLoadingKB(true);
        setError('');
        try {
            const response = await axiosInstance.get(`/api/gmail/knowledge-base`, {
                params: { chatbot_id: chatbotId }
            });
            setKnowledgeBaseItems(response.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load knowledge base');
        } finally {
            setLoadingKB(false);
        }
    };

    const handleKBItemToggle = (itemId) => {
        setSelectedKBItems(prev => {
            if (prev.includes(itemId)) {
                return prev.filter(id => id !== itemId);
            } else {
                return [...prev, itemId];
            }
        });
    };

    const handleGenerateReplies = async () => {
        setGeneratingReply(true);
        setError('');
        setActiveStep(1); // Move to loading screen
        
        try {
            const replies = {};
            for (const email of selectedEmails) {
                const response = await axiosInstance.post(`/api/gmail/generate-reply`, {
                    message_db_id: email.id,
                    use_knowledge_base: dataSource === 'knowledge_base',
                    selected_kb_items: dataSource === 'knowledge_base' ? selectedKBItems : []
                }, {
                    params: { chatbot_id: chatbotId }
                });
                
                if (response.data.success) {
                    replies[email.id] = response.data.reply;
                } else {
                     throw new Error(response.data.message || `Failed to generate reply for ${email.subject}`);
                }
            }
            setGeneratedReplies(replies);
            setActiveStep(2); // Move to review step
        } catch (err) {
            setError(err.message || 'An error occurred while generating replies.');
            setActiveStep(0); // On error, return to configuration step
        } finally {
            setGeneratingReply(false);
        }
    };

    const handleNextOnly = () => {
        if (currentEmailIndex < selectedEmails.length - 1) {
            setCurrentEmailIndex(prev => prev + 1);
        } else {
            setActiveStep(3); // Move to training step
        }
    };

    const handleSaveDraft = async () => {
        setSavingDraft(true);
        setError('');
        
        try {
            const currentEmail = selectedEmails[currentEmailIndex];
            const replyBody = editor.getHTML();
            
            await axiosInstance.post(`/api/gmail/save-draft`, {
                message_db_id: currentEmail.id,
                body: replyBody,
            }, {
                params: { chatbot_id: chatbotId }
            });
            
            // On success, move to the next email
            handleNextOnly();

        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save draft');
        } finally {
            setSavingDraft(false);
        }
    };

    const handleSendReplies = async () => {
        setSendingReplies(true);
        setError('');
        
        try {
            const currentEmail = selectedEmails[currentEmailIndex];
            const replyBody = editor.getHTML();
            
            await axiosInstance.post(`/api/gmail/send-reply`, {
                thread_id: currentEmail.thread_id,
                subject: currentEmail.subject,
                to_address: currentEmail.from_address,
                body: replyBody,
                message_db_id: currentEmail.id,
            }, {
                params: { chatbot_id: chatbotId }
            });
            
            // Move to next email or finish
            handleNextOnly();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send reply');
        } finally {
            setSendingReplies(false);
        }
    };

    const handleTrainAI = async () => {
        setTrainingAI(true);
        setError('');
        
        try {
            const message_db_ids = selectedEmails.map(email => email.id);
            await axiosInstance.post(`/api/gmail/bulk-train`, {
                message_db_ids
            }, {
                params: { chatbot_id: chatbotId }
            });
            
            onSuccess();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to train AI');
        } finally {
            setTrainingAI(false);
        }
    };

    const handleSkipTraining = () => {
        onSuccess();
        onClose();
    };

    const formatAiReplyForEditor = (text) => {
        if (!text) return '<p></p>';
        const paragraphs = text.trim().split('\n').filter(line => line.trim() !== '').map(line => `<p>${line.trim()}</p>`);
        return paragraphs.join('');
    };

    const handleExpandToggle = (itemId) => {
        setExpandedKBItem(prev => (prev === itemId ? null : itemId));
    };

    const filteredKnowledgeBaseItems = useMemo(() => {
        if (!kbSearchQuery) {
            return knowledgeBaseItems;
        }
        const lowercasedQuery = kbSearchQuery.toLowerCase();
        return knowledgeBaseItems.filter(item =>
            (item.title && item.title.toLowerCase().includes(lowercasedQuery)) ||
            (item.url && item.url.toLowerCase().includes(lowercasedQuery)) ||
            (item.summary && item.summary.toLowerCase().includes(lowercasedQuery)) ||
            (item.content_preview && item.content_preview.toLowerCase().includes(lowercasedQuery))
        );
    }, [knowledgeBaseItems, kbSearchQuery]);

    const renderStepContent = () => {
        switch (activeStep) {
            case 0: // Select Data Source
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Choose how to generate email replies
                        </Typography>
                        
                        <FormControl component="fieldset">
                            <RadioGroup
                                value={dataSource}
                                onChange={(e) => setDataSource(e.target.value)}
                            >
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    {/* Existing Chatbot Data Option */}
                                    <Box sx={{
                                        flex: '1 1 300px',
                                        border: '3px solid',
                                        borderColor: dataSource === 'existing' ? '#28a745' : '#dee2e6',
                                        borderRadius: 2,
                                        p: 3,
                                        backgroundColor: dataSource === 'existing' ? '#d4edda' : '#f8f9fa',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                    onClick={() => setDataSource('existing')}
                                    >
                                        <FormControlLabel
                                            value="existing"
                                            control={
                                                <Radio sx={{
                                                    '&.Mui-checked': { color: '#28a745' }
                                                }} />
                                            }
                                            label={
                                                <div>
                                                    <h5 className="mb-2">
                                                        <i className="fa fa-database me-2" style={{ color: '#28a745' }}></i>
                                                        Existing Chatbot Data
                                                    </h5>
                                                    <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                                        Use all trained data from your chatbot's knowledge base (automatic matching)
                                                    </p>
                                                </div>
                                            }
                                            sx={{ width: '100%', m: 0 }}
                                        />
                                    </Box>

                                    {/* Knowledge Base Option */}
                                    <Box sx={{
                                        flex: '1 1 300px',
                                        border: '3px solid',
                                        borderColor: dataSource === 'knowledge_base' ? '#0d6efd' : '#dee2e6',
                                        borderRadius: 2,
                                        p: 3,
                                        backgroundColor: dataSource === 'knowledge_base' ? '#cfe2ff' : '#f8f9fa',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                    onClick={() => setDataSource('knowledge_base')}
                                    >
                                        <FormControlLabel
                                            value="knowledge_base"
                                            control={
                                                <Radio sx={{
                                                    '&.Mui-checked': { color: '#0d6efd' }
                                                }} />
                                            }
                                            label={
                                                <div>
                                                    <h5 className="mb-2">
                                                        <i className="fa fa-book me-2" style={{ color: '#0d6efd' }}></i>
                                                        Select from Knowledge Base
                                                    </h5>
                                                    <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                                        Manually select specific documents, websites, or images to use
                                                    </p>
                                                </div>
                                            }
                                            sx={{ width: '100%', m: 0 }}
                                        />
                                    </Box>
                                </Box>
                            </RadioGroup>
                        </FormControl>

                        {dataSource === 'knowledge_base' && (
                            <Box sx={{ mt: 3 }}>
                                <Divider sx={{ mb: 2 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                                    <Typography variant="h6">
                                        Select Knowledge Base Items ({selectedKBItems.length} selected)
                                    </Typography>
                                    <TextField
                                        size="small"
                                        variant="outlined"
                                        placeholder="Search knowledge..."
                                        value={kbSearchQuery}
                                        onChange={(e) => setKbSearchQuery(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ width: { xs: '100%', sm: '300px' } }}
                                    />
                                </Box>
                                
                                {loadingKB ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <List sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #dee2e6', borderRadius: 1, p: 0 }}>
                                        {filteredKnowledgeBaseItems.length === 0 ? (
                                            <ListItem>
                                                <ListItemText primary={kbSearchQuery ? "No results found." : "No knowledge base items available"} />
                                            </ListItem>
                                        ) : (
                                            filteredKnowledgeBaseItems.map((item) => (
                                                <React.Fragment key={item.id}>
                                                    <ListItemButton onClick={() => handleExpandToggle(item.id)} divider>
                                                        <ListItemIcon sx={{ minWidth: 0, mr: 1.5 }}>
                                                            <Checkbox
                                                                edge="start"
                                                                checked={selectedKBItems.includes(item.id)}
                                                                tabIndex={-1}
                                                                disableRipple
                                                                onChange={() => handleKBItemToggle(item.id)}
                                                                onClick={(e) => e.stopPropagation()} // Prevent expansion when clicking checkbox
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Chip
                                                                        label={item.type}
                                                                        size="small"
                                                                        color={
                                                                            item.type === 'website' ? 'primary' :
                                                                            item.type === 'document' ? 'secondary' : 'default'
                                                                        }
                                                                    />
                                                                    <Typography variant="body1" component="span" sx={{ fontWeight: 500 }}>
                                                                        {item.title}
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                            secondary={item.type === 'website' && item.url ? item.url : null}
                                                            secondaryTypographyProps={{ noWrap: true, sx: { pr: 4 } }}
                                                        />
                                                        <IconButton edge="end" size="small" sx={{ position: 'absolute', right: 16 }}>
                                                            {expandedKBItem === item.id ? <ExpandLess /> : <ExpandMore />}
                                                        </IconButton>
                                                    </ListItemButton>
                                                    <Collapse in={expandedKBItem === item.id} timeout="auto" unmountOnExit>
                                                        <Box sx={{ p: 2, bgcolor: '#fafafa', borderBottom: '1px solid #dee2e6' }}>
                                                            {item.summary && (
                                                                <Typography variant="body2" color="text.secondary" paragraph>
                                                                    <strong>Summary:</strong> {item.summary}
                                                                </Typography>
                                                            )}
                                                            {item.content_preview && (
                                                                <Typography variant="body2" color="text.secondary" sx={{
                                                                    wordBreak: 'break-word',
                                                                    whiteSpace: 'pre-wrap',
                                                                }}>
                                                                    <strong>Content Preview:</strong> {item.content_preview}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Collapse>
                                                </React.Fragment>
                                            ))
                                        )}
                                    </List>
                                )}
                            </Box>
                        )}
                    </Box>
                );

            case 1: // Generate Reply
                return (
                    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Generating AI replies for {selectedEmails.length} email(s)...
                        </Typography>
                        {dataSource === 'knowledge_base' && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Using {selectedKBItems.length} selected knowledge base item(s) to generate replies.
                            </Alert>
                        )}
                        <CircularProgress size={60} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Please wait, this may take a moment.
                        </Typography>
                    </Box>
                );

            case 2: // Review & Send
                const currentEmail = selectedEmails[currentEmailIndex];
                if (!currentEmail) return null; // Guard against empty selection
                return (
                    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>
                            To: {currentEmail?.from_address} | Subject: Re: {currentEmail?.subject}
                        </Typography>
                        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                           <TiptapEditor editor={editor} height="100%" />
                        </Box>
                    </Box>
                );

            case 3: // Train AI
                return (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Train AI with these replies?
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Training the AI will help it learn from these replies and improve future suggestions.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                onClick={handleSkipTraining}
                                disabled={trainingAI}
                            >
                                Skip Training
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleTrainAI}
                                disabled={trainingAI}
                            >
                                {trainingAI ? <CircularProgress size={24} color="inherit" /> : 'Train AI & Finish'}
                            </Button>
                        </Box>
                    </Box>
                );

            default:
                return null;
        }
    };

    const handleBack = () => {
        if (activeStep === 2) {
            setActiveStep(0); // Go all the way back to re-configure if needed
            setGeneratedReplies({});
            setCurrentEmailIndex(0);
        } else if (activeStep > 0) {
            setActiveStep(prev => prev - 1);
        }
    };

    return (
        <MuiModal open={isOpen} onClose={() => onClose(false)}>
            <Box sx={modalStyle}>
                <Typography variant="h5" sx={{ mb: 3, flexShrink: 0 }}>
                    Email Reply Wizard
                </Typography>
                
                <Box sx={{ flexShrink: 0 }}>
                    <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {error && (
                        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                </Box>

                <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
                    {renderStepContent()}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, borderTop: '1px solid #dee2e6', flexShrink: 0 }}>
                    <Button
                        onClick={handleBack}
                        disabled={activeStep === 0 || activeStep === 1 || generatingReply || sendingReplies || trainingAI || savingDraft}
                    >
                        Back
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={() => onClose(false)} disabled={generatingReply || sendingReplies || trainingAI || savingDraft}>
                            Cancel
                        </Button>
                        
                        {activeStep === 0 && (
                            <Button
                                variant="contained"
                                onClick={handleGenerateReplies}
                                disabled={
                                    generatingReply || 
                                    (dataSource === 'knowledge_base' && selectedKBItems.length === 0)
                                }
                            >
                                Next
                            </Button>
                        )}
                        
                        {activeStep === 2 && (
                            <>
                                <Button
                                    variant="outlined"
                                    onClick={handleSaveDraft}
                                    disabled={generatingReply || sendingReplies || trainingAI || savingDraft}
                                >
                                    {savingDraft ? <CircularProgress size={24} color="inherit" /> : 'Save to Draft'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleNextOnly}
                                    disabled={generatingReply || sendingReplies || trainingAI || savingDraft}
                                >
                                    Next
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSendReplies}
                                    disabled={generatingReply || sendingReplies || trainingAI || savingDraft}
                                >
                                    Send Email ({currentEmailIndex + 1}/{selectedEmails.length})
                                    {sendingReplies && <CircularProgress size={24} sx={{ ml: 1, color: 'white' }} />}
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </MuiModal>
    );
}